import { useState, useEffect, type ReactNode, type ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { Flex } from '@magnetic/flex'
import { Container } from '@magnetic/container'
import { Card } from '@magnetic/card'
import { Text } from '@magnetic/text'
import { FilterBar } from '@magnetic/filter-bar'
import { Search } from '@magnetic/search'
import { Skeleton } from '@magnetic/skeleton'
import { Spinner } from '@magnetic/spinner'
import { Divider } from '@magnetic/divider'
import { Table, useMagneticTable, getPaginationRowModel, getSelectionColumn, type MdsTableColumn } from '@magnetic/table'
import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Positive, Negative, Information, Users as UsersIcon } from '@/lib/icons'
import type { ListProjectsResponse } from '@/gen/catpocs/v1/projects_pb'
import type { ListAllProjectsResponse } from '@/gen/catpocs/v1/admin_pb'
import { listAllProjects, listProjects } from '@/lib/api'

type ProjectRecord = ListProjectsResponse['projects'][number] | ListAllProjectsResponse['projects'][number]

interface ProjectRow {
  id: string
  name: string
  displayName?: string
  namespace: string
  applicationCount: number
  healthyCount?: number
  unhealthyCount?: number
  owners: string[]
  createdAt?: { seconds: bigint | number | string }
}

interface ProjectsTableProps {
  // RPC query function to use (listProjects or listAllProjects)
  queryFn: typeof listProjects | typeof listAllProjects
  // Optional title
  title?: string
  // Optional description
  description?: string
  // Enable row selection
  selectableRows?: boolean
  // Custom configuration components (buttons) for the FilterBar
  configComponents?: ReactElement | null
  // Custom bulk actions for selected rows - receives selected count and selected rows
  bulkActions?: (selectedCount: number, selectedRows: ProjectRow[]) => ReactNode
  // Initial page size
  initialPageSize?: number
}

export function ProjectsTable({
  queryFn,
  title,
  description,
  selectableRows = false,
  configComponents,
  bulkActions,
  initialPageSize = 20,
}: ProjectsTableProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [showReset, setShowReset] = useState(false)

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
      setCurrentPage(1) // Reset to first page on search
      setShowReset(searchQuery !== '')
    }, 500)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Calculate page token (skip value)
  const pageToken = currentPage > 1 ? String((currentPage - 1) * pageSize) : ''
  const isAllProjectsQuery = queryFn === listAllProjects

  // Query for user-scoped projects
  const userProjectsQuery = useConnectQuery(listProjects, {
    pageSize: pageSize,
    pageToken: pageToken,
    tags: [],
  }, {
    enabled: !isAllProjectsQuery,
  })

  // Query for admin all-projects view
  const allProjectsQuery = useConnectQuery(listAllProjects, {
    search: debouncedSearch,
    pageSize: pageSize,
    pageToken: pageToken,
  }, {
    enabled: isAllProjectsQuery,
  })

  const rawData = isAllProjectsQuery ? allProjectsQuery.data : userProjectsQuery.data
  const isLoading = isAllProjectsQuery ? allProjectsQuery.isLoading : userProjectsQuery.isLoading
  const error = isAllProjectsQuery ? allProjectsQuery.error : userProjectsQuery.error

  const response = rawData as ListProjectsResponse | ListAllProjectsResponse | undefined
  const projects: ProjectRow[] = (response?.projects ?? []).map((project: ProjectRecord) => ({
    id: project.id,
    name: project.name,
    displayName: 'displayName' in project ? project.displayName : undefined,
    namespace: project.namespace,
    applicationCount: project.applicationCount,
    healthyCount: 'healthyCount' in project ? project.healthyCount : undefined,
    unhealthyCount: 'unhealthyCount' in project ? project.unhealthyCount : undefined,
    owners: project.owners,
    createdAt: project.createdAt ? { seconds: project.createdAt.seconds } : undefined,
  }))
  const totalCount = response?.totalCount ?? 0

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setCurrentPage(1)
  }

  const handleReset = () => {
    setSearchQuery('')
    setDebouncedSearch('')
    setShowReset(false)
    setCurrentPage(1)
  }

  // Define columns
  const tableColumns: MdsTableColumn<ProjectRow>[] = [
    // Add selection column if selectable rows are enabled
    ...(selectableRows ? [getSelectionColumn<ProjectRow>()] : []),
    {
      id: 'name',
      header: 'Project',
      accessorKey: 'displayName',
      cell: ({ row }) => (
        <Link
          to={`/projects/${row.original.id}`}
          style={{
            color: 'var(--interact-text-strong-default)',
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          {row.original.displayName || row.original.name}
        </Link>
      ),
    },
    {
      id: 'namespace',
      header: 'Namespace',
      accessorKey: 'namespace',
      cell: ({ getValue }) => (
        <Text size="sm" family="mono" color="secondary">
          {getValue() as string}
        </Text>
      ),
    },
    {
      id: 'applications',
      header: 'Applications',
      accessorKey: 'applicationCount',
      cell: ({ getValue }) => <Text weight="semibold">{getValue() as number}</Text>,
    },
    {
      id: 'health',
      header: 'Health',
      cell: ({ row }) => {
        const totalApps = row.original.applicationCount || 0
        const healthyApps = row.original.healthyCount || 0
        const unhealthyApps = row.original.unhealthyCount || 0
        const unknownApps = totalApps - healthyApps - unhealthyApps

        if (totalApps === 0) {
          return <Text size="sm" color="tertiary">No apps</Text>
        }

        return (
          <Flex gap={12} align="center">
            {healthyApps > 0 && (
              <Flex gap={4} align="center">
                <Positive hasBackground />
                <Text size="sm">{healthyApps}</Text>
              </Flex>
            )}
            {unhealthyApps > 0 && (
              <Flex gap={4} align="center">
                <Negative hasBackground />
                <Text size="sm">{unhealthyApps}</Text>
              </Flex>
            )}
            {unknownApps > 0 && (
              <Flex gap={4} align="center">
                <Information hasBackground />
                <Text size="sm">{unknownApps}</Text>
              </Flex>
            )}
          </Flex>
        )
      },
    } as MdsTableColumn<ProjectRow>,
    {
      id: 'owners',
      header: 'Owners',
      accessorKey: 'owners',
      cell: ({ getValue }) => {
        const owners = getValue() as string[]
        return (
          <Flex gap={4} align="center">
            <UsersIcon size={16} weight="bold" color="var(--interact-icon-weak-default)" />
            <Text size="sm">{owners.length}</Text>
          </Flex>
        )
      },
    },
    {
      id: 'created',
      header: 'Created',
      accessorKey: 'createdAt',
      cell: ({ getValue }) => {
        const createdAt = getValue() as { seconds: bigint | string } | undefined
        return (
          <Text size="sm" color="secondary">
            {createdAt
              ? new Date(Number(createdAt.seconds) * 1000).toLocaleDateString()
              : 'N/A'}
          </Text>
        )
      },
    },
  ]

  const pageCount = totalCount ? Math.ceil(totalCount / pageSize) : -1

  const { table } = useMagneticTable({
    columns: tableColumns,
    data: projects,
    // Row selection
    enableRowSelection: selectableRows,
    // Server-side pagination
    manualPagination: totalCount !== undefined,
    pageCount: pageCount,
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      pagination: {
        pageIndex: currentPage - 1,
        pageSize: pageSize,
      }
    },
    onPaginationChange: (updater) => {
      if (typeof updater === 'function') {
        const newState = updater({
          pageIndex: currentPage - 1,
          pageSize: pageSize
        })

        if (newState.pageIndex !== currentPage - 1) {
          handlePageChange(newState.pageIndex + 1)
        }

        if (newState.pageSize !== pageSize) {
          handlePageSizeChange(newState.pageSize)
        }
      }
    },
  })

  const showPagination = totalCount > 0

  return (
    <Flex direction="vertical" gap={24} style={{ width: '100%' }}>
      {/* Title and Description */}
      {(title || description) && (
        <Flex direction="vertical" gap={8}>
          {title && <Text size="h2" weight="bold">{title}</Text>}
          {description && <Text color="secondary">{description}</Text>}
        </Flex>
      )}

      {/* FilterBar */}
      <Container>
        <Flex direction="vertical" gap={16}>
          <FilterBar
            searchBar={
              <Search
                fixedWidth
                label="Search projects"
                placeholder="Search by name, namespace, owner..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClear={searchQuery ? () => setSearchQuery('') : undefined}
                size="md"
              />
            }
            results={totalCount}
            isLoadingResults={isLoading}
            showResetButton={showReset}
            onReset={handleReset}
            configComponents={configComponents ?? undefined}
          >
            {/* Future filters can be added here as children */}
            <Flex />
          </FilterBar>
          <Card>
            {/* Initial load: Show skeleton (>1s, initial page render) */}
            {isLoading && !debouncedSearch && (
              <Container>
                <Flex direction="vertical">
                  {/* Table header skeleton */}
                  <Flex gap={36} style={{ marginLeft: 12, marginRight: 12, paddingTop: 16, paddingBottom: 16 }}>
                    <Skeleton height="sm" shade="dark" style={{ width: '25%' }} />
                    <Skeleton height="sm" shade="dark" style={{ width: '15%' }} />
                    <Skeleton height="sm" shade="dark" style={{ width: '15%' }} />
                    <Skeleton height="sm" shade="dark" style={{ width: '20%' }} />
                    <Skeleton height="sm" shade="dark" style={{ width: '15%' }} />
                  </Flex>
                  {/* Table rows skeleton */}
                  {[...Array(8)].map((_, index) => (
                    <Flex key={index} direction="vertical">
                      <Divider size="sm" />
                      <Flex gap={36} style={{ marginLeft: 12, marginRight: 12, paddingTop: 16, paddingBottom: 16 }}>
                        <Skeleton height="sm" style={{ width: '25%' }} />
                        <Skeleton height="sm" style={{ width: '15%' }} />
                        <Skeleton height="sm" style={{ width: '15%' }} />
                        <Skeleton height="sm" style={{ width: '20%' }} />
                        <Skeleton height="sm" style={{ width: '15%' }} />
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              </Container>
            )}

            {/* Search/filter update: Show spinner (user action, partial update) */}
            {isLoading && debouncedSearch && (
              <Flex justify="center" align="center" style={{ padding: 48 }}>
                <Spinner size="md" />
              </Flex>
            )}

            {error && (
              <Flex direction="vertical" gap={8} align="center" style={{ padding: '32px' }}>
                <Negative hasBackground />
                <Text color="secondary">Failed to load projects</Text>
                <Text size="sm" color="tertiary">{error.message}</Text>
              </Flex>
            )}

            {!isLoading && !error && projects.length === 0 && (
              <Flex direction="vertical" gap={8} align="center" style={{ padding: '32px' }}>
                <Information hasBackground />
                <Text color="secondary">
                  {searchQuery ? 'No projects match your search' : 'No projects found'}
                </Text>
              </Flex>
            )}

            {!isLoading && !error && projects.length > 0 && (
              <Table
                table={table}
                selectableRows={selectableRows}
                paginationControls={showPagination ? {} : undefined}
              >
                {selectableRows && bulkActions && (
                  <Table.BulkActionBar>
                    <Flex direction="horizontal" gap={8} align="center">
                      {bulkActions(
                        table.getSelectedRowModel().rows.length,
                        table.getSelectedRowModel().rows.map(row => row.original)
                      )}
                    </Flex>
                  </Table.BulkActionBar>
                )}
              </Table>
            )}
          </Card>
        </Flex>
      </Container>
    </Flex>
  )
}
